import React, { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import {
  Menu,
  PanelLeftClose,
  PanelLeftOpen,
  Bell,
  ChevronDown,
  User,
  Settings,
  LogOut,
  Building2,
} from 'lucide-react'
import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import { cn } from '@/lib/utils'
import { Avatar } from '@/components/ui/Avatar'
import { useAuthStore } from '@/store/authStore'
import { useUnreadCount, useMyNotifications, useMarkAsRead, useMarkAllAsRead } from '@/lib/api/notifications'
import { formatDistanceToNow } from 'date-fns'
import { tr } from 'date-fns/locale'

interface HeaderProps {
  collapsed:       boolean
  onToggleCollapse: () => void
  onMobileMenuOpen: () => void
}

// Mock branches — replace with real data from API
const branches = [
  { id: '1', name: 'Merkez Şube' },
  { id: '2', name: 'Kadıköy Şubesi' },
  { id: '3', name: 'Beşiktaş Şubesi' },
]

export const Header: React.FC<HeaderProps> = ({
  collapsed,
  onToggleCollapse,
  onMobileMenuOpen,
}) => {
  const navigate    = useNavigate()
  const { user, logout } = useAuthStore()
  const [activeBranch, setActiveBranch] = useState(branches[0])

  const { data: unreadData } = useUnreadCount()
  const { data: recentNotifs } = useMyNotifications({ page: 1, pageSize: 5 })
  const markAsRead = useMarkAsRead()
  const markAllAsRead = useMarkAllAsRead()
  const notifCount = unreadData?.count ?? 0
  const recentItems = recentNotifs?.items ?? []

  const handleLogout = () => {
    logout()
    navigate('/giris', { replace: true })
  }

  return (
    <header
      className={cn(
        'fixed top-0 right-0 z-header h-16',
        'bg-white/90 dark:bg-slate-900/90 backdrop-blur-md',
        'border-b border-slate-200 dark:border-slate-700',
        'flex items-center gap-3 px-4',
        'transition-all duration-300',
        // Offset by sidebar width
        collapsed ? 'lg:left-[72px]' : 'lg:left-[260px]',
        'left-0',
      )}
      role="banner"
    >
      {/* Mobile hamburger */}
      <button
        onClick={onMobileMenuOpen}
        className="lg:hidden p-2 rounded-lg text-slate-500 hover:text-slate-700 hover:bg-slate-100 dark:hover:text-slate-300 dark:hover:bg-slate-700 transition-colors"
        aria-label="Menüyü aç"
      >
        <Menu className="h-5 w-5" />
      </button>

      {/* Desktop sidebar toggle */}
      <button
        onClick={onToggleCollapse}
        className="hidden lg:flex p-2 rounded-lg text-slate-500 hover:text-slate-700 hover:bg-slate-100 dark:hover:text-slate-300 dark:hover:bg-slate-700 transition-colors"
        aria-label={collapsed ? 'Menüyü genişlet' : 'Menüyü daralt'}
        aria-expanded={!collapsed}
      >
        {collapsed
          ? <PanelLeftOpen className="h-5 w-5" />
          : <PanelLeftClose className="h-5 w-5" />
        }
      </button>

      {/* ── Branch Selector ──────────────────────────────── */}
      <DropdownMenu.Root>
        <DropdownMenu.Trigger asChild>
          <button
            className={cn(
              'hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm',
              'border border-slate-200 dark:border-slate-700',
              'bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300',
              'hover:bg-slate-50 dark:hover:bg-slate-700',
              'transition-colors duration-150',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500',
            )}
            aria-label="Şube seç"
          >
            <Building2 className="h-4 w-4 text-slate-400 flex-shrink-0" aria-hidden="true" />
            <span className="font-medium max-w-[140px] truncate">{activeBranch.name}</span>
            <ChevronDown className="h-3.5 w-3.5 text-slate-400 flex-shrink-0" aria-hidden="true" />
          </button>
        </DropdownMenu.Trigger>

        <DropdownMenu.Portal>
          <DropdownMenu.Content
            sideOffset={4}
            align="start"
            className={cn(
              'z-tooltip min-w-[200px] rounded-xl',
              'bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 shadow-card-lg',
              'p-1.5 animate-slide-up',
            )}
          >
            <p className="px-2 py-1.5 text-xs font-semibold text-slate-400 uppercase tracking-wider">
              Şubeler
            </p>
            {branches.map((b) => (
              <DropdownMenu.Item
                key={b.id}
                onSelect={() => setActiveBranch(b)}
                className={cn(
                  'flex items-center gap-2 px-3 py-2 text-sm rounded-lg cursor-pointer',
                  'focus:outline-none',
                  b.id === activeBranch.id
                    ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-400 font-medium'
                    : 'text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700',
                )}
              >
                <Building2 className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
                {b.name}
                {b.id === activeBranch.id && (
                  <span className="ml-auto w-1.5 h-1.5 rounded-full bg-primary-500 flex-shrink-0" />
                )}
              </DropdownMenu.Item>
            ))}
          </DropdownMenu.Content>
        </DropdownMenu.Portal>
      </DropdownMenu.Root>

      <div className="flex-1" />

      {/* ── Notifications ───────────────────────────────── */}
      <DropdownMenu.Root>
        <DropdownMenu.Trigger asChild>
          <button
            className={cn(
              'relative p-2 rounded-lg',
              'text-slate-500 hover:text-slate-700 dark:hover:text-slate-300',
              'hover:bg-slate-100 dark:hover:bg-slate-700',
              'transition-colors duration-150',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500',
            )}
            aria-label={`Bildirimler, ${notifCount} okunmamış`}
          >
            <Bell className="h-5 w-5" />
            {notifCount > 0 && (
              <span
                className="absolute top-1.5 right-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-sport-red text-white text-2xs font-bold px-1"
                aria-hidden="true"
              >
                {notifCount > 9 ? '9+' : notifCount}
              </span>
            )}
          </button>
        </DropdownMenu.Trigger>

        <DropdownMenu.Portal>
          <DropdownMenu.Content
            sideOffset={4}
            align="end"
            className={cn(
              'z-tooltip w-80 rounded-xl',
              'bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 shadow-card-lg',
              'p-0 animate-slide-up overflow-hidden',
            )}
          >
            <div className="px-4 py-3 border-b border-slate-100 dark:border-slate-700 flex items-center justify-between">
              <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">Bildirimler</p>
              {notifCount > 0 && (
                <button
                  className="text-xs text-primary-600 hover:underline"
                  onClick={() => markAllAsRead.mutate()}
                >
                  Tümünü okundu işaretle
                </button>
              )}
            </div>
            <div className="divide-y divide-slate-100 dark:divide-slate-700 max-h-64 overflow-y-auto">
              {recentItems.length === 0 ? (
                <div className="px-4 py-6 text-center text-sm text-slate-400">
                  Henüz bildiriminiz yok
                </div>
              ) : (
                recentItems.map((n) => (
                  <DropdownMenu.Item
                    key={n.id}
                    className={cn(
                      'px-4 py-3 cursor-pointer focus:outline-none transition-colors',
                      n.isRead
                        ? 'hover:bg-slate-50 dark:hover:bg-slate-700'
                        : 'bg-primary-50/40 dark:bg-primary-900/10 hover:bg-primary-50 dark:hover:bg-primary-900/20',
                    )}
                    onSelect={() => {
                      if (!n.isRead) markAsRead.mutate(n.id)
                      if (n.link) navigate(n.link)
                    }}
                  >
                    <div className="flex items-start gap-3">
                      {!n.isRead && (
                        <span className="mt-1.5 h-2 w-2 rounded-full bg-primary-500 flex-shrink-0" />
                      )}
                      <div className={cn(n.isRead && 'pl-5')}>
                        <p className="text-sm font-medium text-slate-700 dark:text-slate-300">{n.title}</p>
                        <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5 line-clamp-1">{n.body}</p>
                        <p className="text-xs text-slate-400 mt-0.5">
                          {formatDistanceToNow(new Date(n.createdAt), { addSuffix: true, locale: tr })}
                        </p>
                      </div>
                    </div>
                  </DropdownMenu.Item>
                ))
              )}
            </div>
            <div className="px-4 py-2.5 border-t border-slate-100 dark:border-slate-700">
              <Link
                to="/bildirimler"
                className="text-xs text-primary-600 hover:underline block text-center"
              >
                Tüm bildirimleri gör
              </Link>
            </div>
          </DropdownMenu.Content>
        </DropdownMenu.Portal>
      </DropdownMenu.Root>

      {/* ── User Menu ────────────────────────────────────── */}
      <DropdownMenu.Root>
        <DropdownMenu.Trigger asChild>
          <button
            className={cn(
              'flex items-center gap-2 pl-1 pr-2 py-1 rounded-xl',
              'hover:bg-slate-100 dark:hover:bg-slate-700',
              'transition-colors duration-150',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500',
            )}
            aria-label="Kullanıcı menüsü"
          >
            <Avatar
              src={user?.profilePhotoUrl}
              name={user ? `${user.firstName} ${user.lastName}` : 'Kullanıcı'}
              size="sm"
            />
            <div className="hidden sm:block text-left">
              <p className="text-xs font-semibold text-slate-800 dark:text-slate-200 leading-none">
                {user ? `${user.firstName} ${user.lastName}` : 'Kullanıcı'}
              </p>
              <p className="text-2xs text-slate-400 mt-0.5">{user?.role ?? 'Admin'}</p>
            </div>
            <ChevronDown className="hidden sm:block h-3.5 w-3.5 text-slate-400" aria-hidden="true" />
          </button>
        </DropdownMenu.Trigger>

        <DropdownMenu.Portal>
          <DropdownMenu.Content
            sideOffset={4}
            align="end"
            className={cn(
              'z-tooltip w-56 rounded-xl',
              'bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 shadow-card-lg',
              'p-1.5 animate-slide-up',
            )}
          >
            <div className="px-3 py-2 mb-1">
              <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                {user ? `${user.firstName} ${user.lastName}` : 'Kullanıcı'}
              </p>
              <p className="text-xs text-slate-400 truncate">{user?.email}</p>
            </div>

            <DropdownMenu.Separator className="my-1 h-px bg-slate-100 dark:bg-slate-700" />

            <DropdownMenu.Item
              onSelect={() => navigate('/profil')}
              className="flex items-center gap-2 px-3 py-2 text-sm rounded-lg cursor-pointer text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 focus:outline-none"
            >
              <User className="h-4 w-4" aria-hidden="true" />
              Profilim
            </DropdownMenu.Item>

            <DropdownMenu.Item
              onSelect={() => navigate('/ayarlar')}
              className="flex items-center gap-2 px-3 py-2 text-sm rounded-lg cursor-pointer text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 focus:outline-none"
            >
              <Settings className="h-4 w-4" aria-hidden="true" />
              Ayarlar
            </DropdownMenu.Item>

            <DropdownMenu.Separator className="my-1 h-px bg-slate-100 dark:bg-slate-700" />

            <DropdownMenu.Item
              onSelect={handleLogout}
              className="flex items-center gap-2 px-3 py-2 text-sm rounded-lg cursor-pointer text-sport-red hover:bg-red-50 dark:hover:bg-red-900/20 focus:outline-none"
            >
              <LogOut className="h-4 w-4" aria-hidden="true" />
              Çıkış Yap
            </DropdownMenu.Item>
          </DropdownMenu.Content>
        </DropdownMenu.Portal>
      </DropdownMenu.Root>
    </header>
  )
}
